'use client';

import { alpha, Button, Paper, Stack, styled, Typography } from "@mui/material";
import { useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import Link from "next/link";
import PopupCloseButton from "@/app/features/sites/announcements-popup/ui/PopupCloseButton";
import { usePathname } from "next/navigation";
import { Announcement, Enum_Componentlinkslink_Type } from "@/app/lib/generated/graphql-types";
import { Markdown } from "@/app/features/shared/markdown/Markdown";
import useIsDesktop from "@/hooks/useDesktop";
import { signatureBorderStyle } from "@/app/theme/theme-tokens";
import VisiblyScrollable from "@/app/features/shared/__to_be_integrated/ui/v2/VisiblyScrollable";

const LOCAL_KEY = "announcement-popup-acknowledged";
const ANIMATION_DURATION = 0.75;
const INITIAL_DELAY = 7;

const rememberUser = (dismissalVersion: number) => localStorage.setItem(LOCAL_KEY, String(dismissalVersion));

const hasAcknowledgedVersion = (dismissalVersion: number) => {
    const storedValue = localStorage.getItem(LOCAL_KEY);
    if (storedValue === 'true') return dismissalVersion === 0;

    const acknowledgedVersion = Number(storedValue);
    return Number.isFinite(acknowledgedVersion) && acknowledgedVersion >= dismissalVersion;
};

interface AnnouncementPopupViewProps {
    data: Omit<Announcement, 'localizations'>,
    previewMode?: boolean
}

export default function AnnouncementPopupView({ data, previewMode = false }: AnnouncementPopupViewProps) {
    const isDesktop = useIsDesktop();
    const path = usePathname();

    const isPopupRoute = path === '/';
    const [dismissedPath, setDismissedPath] = useState<string | null>(null);
    const [isAcknowledged, setIsAcknowledged] = useState<boolean>(() => {
        if (typeof window === "undefined") return false;
        return hasAcknowledgedVersion(data.dismissalVersion ?? 0);
    });

    const onDismiss = () => {
        setDismissedPath(path);
    };

    const onNeverShowAgain = () => {
        rememberUser(data.dismissalVersion ?? 0);
        setIsAcknowledged(true);
        setDismissedPath(path);
    }

    const variants = {
        hidden: {
            opacity: 0,
            x: '100vw',
            zIndex: 99,
        },
        visible: {
            opacity: 1,
            x: 0,
            zIndex: 999,
        },
    }

    const animationProps = previewMode
        ? {}
        : {
            variants,
            initial: "hidden" as const,
            animate: "visible" as const,
            exit: {
                ...variants.hidden,
                transition: { ease: 'easeIn', duration: 0.3 }
            },
            transition: { duration: ANIMATION_DURATION, ease: "easeInOut", delay: INITIAL_DELAY },
        };

    const shouldRenderPopup = previewMode || (isPopupRoute && !isAcknowledged && dismissedPath !== path);

    return (
        <AnimatePresence>
            {shouldRenderPopup && (
                <AnimatedPaper
                    className="announcement-popup"
                    elevation={8}
                    {...animationProps}
                    sx={(theme) => ({
                        display: 'flex',
                        position: 'fixed',
                        maxHeight: theme.spacing(48),
                        maxWidth: theme.spacing(56),
                        padding: theme.spacing(2),
                        border: `1px solid ${alpha(theme.palette.primary.main, .25)}`,
                        ...signatureBorderStyle,
                        backgroundColor: theme.palette.background.paper,
                        color: theme.palette.text.primary,
                        zIndex: previewMode ? 'unset' : 2000,
                        ...(isDesktop
                                ? {
                                    top: `calc(var(--header-height) + 16px)`,
                                    right: '16px',
                                }
                                : {
                                    top: `calc(var(--header-height) + 16px)`,
                                    left: '16px',
                                    right: '16px',
                                }
                        ),
                        ...(previewMode ? {
                                position: 'relative',
                                top: 'unset',
                                left: 'unset',
                                right: 'unset',
                                bottom: 'unset',
                                display: 'flex',
                                flex: '1 1 auto',
                            } : {}
                        ),
                        // transform: `translateY(var(--header-height))`
                    })}
                >
                    <Stack spacing={2}>
                        <Stack spacing={1}>
                            {data.title && <CenteredTitle variant="h3" color="primary" className="pr-8">{data.title}</CenteredTitle>}
                            {data.subtitle && <CenteredTitle variant="subtitle2" color="text.secondary">{data.subtitle}</CenteredTitle>}
                        </Stack>

                        <VisiblyScrollable fadeSize='sm'>
                            <Markdown
                                typographyOverrides={{ paragraph: "body2" }}
                                value={data.description}
                                slots={{
                                    root: { sx: { textAlign: 'center' } }
                                }}
                            />
                        </VisiblyScrollable>

                        {data.link && (
                            <Button
                                component={Link}
                                href={data.link?.url}
                                target={previewMode || data.link.type === Enum_Componentlinkslink_Type.External ? "_blank" : "_self"}
                                rel="noopener noreferrer"
                                variant="contained"
                                color="primary"
                                className="w-full"
                                sx={{ textTransform: 'uppercase' }}
                            >
                                {data.link?.name}
                            </Button>
                        )}
                    </Stack>
                    {!previewMode && (
                        <PopupCloseButton
                            dismissText="Schließen"
                            neverShowAgainText="Nicht nochmal anzeigen"
                            onDismiss={onDismiss}
                            onNeverShowAgain={onNeverShowAgain}
                        />
                    )}
                </AnimatedPaper>
            )}
        </AnimatePresence>
    )
}

const AnimatedPaper = motion.create(Paper);

const CenteredTitle = styled(Typography)({
    textAlign: 'center',
    '&.MuiTypography-root': {
        marginBottom: 0,
    }
});
